import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import MainCard from "components/MainCard";
import OfferTargetSelect from "./OfferTargetSelect";
import {
  emptyForm,
  buildOfferPayload,
  getStoreId,
  OFFER_TYPES,
  getOfferType
} from "./offerUtils";
import { offerTypeChipSx,couponCodeChipSx } from "components/table/tableChipStyles";
import { brand, radius } from "utils/designTokens";
import CurrencyAmount from "components/currency/CurrencyAmount";
import DirhamSymbol from "components/currency/DirhamSymbol";
import { useTheme } from "@mui/material/styles";
import { getDirhamSymbolSizePx } from "utils/currency";
import { useToast } from "contexts/ToastContext";
import { useCurrency } from "contexts/CurrencyContext";

const offerCancelButtonSx = {
  borderRadius: `${radius.sm}px`,
  px: 2.5,
  textTransform: "none",
  fontWeight: 600,
  color: "text.secondary",
  borderColor: "divider",
  "&:hover": {
    borderColor: brand.borderHover,
    bgcolor: brand.overlay
  }
};

const offerPrimaryButtonSx = {
  borderRadius: `${radius.sm}px`,
  px: 2.5,
  textTransform: "none",
  fontWeight: 600,
  bgcolor: brand.primary,
  boxShadow: "none",
  "&:hover": {
    bgcolor: brand.primaryDark,
    boxShadow: "none"
  }
};

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  Avatar,
  CircularProgress,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Checkbox,
  FormGroup
} from "@mui/material";

import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

dayjs.extend(customParseFormat);

const STEPS = [
  { label: "Offer Type", icon: <LocalOfferIcon /> },
  { label: "Offer Details", icon: <AccessTimeIcon /> },
  { label: "Target", icon: <SettingsIcon /> }
];

const offerTypeHints = {
  discount: "Discount on the entire products",
  item: "Discount on selected products",
  category: "Discount on selected categories"
};

const DURATION_PRESETS = [
  { value: "custom", label: "Custom Date" },
  { value: "unlimited", label: "Unlimited" },
  { value: "24h", label: "24 Hours" },
  { value: "12h", label: "12 Hours" }
];

const PAYMENT_OPTIONS = [
  { value: "card", label: "Card Payment" },
  { value: "cod", label: "Cash on Delivery" },
  { value: "wallet", label: "Wallet" }
];

function StepOfferType({ form, setForm }) {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Offer Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose the type of offer you want to create.
      </Typography>
      <Grid container spacing={2}>
        {OFFER_TYPES.map((t) => {
          const selected = form.offer_type === t.value;
          return (
            <Grid size={{ xs: 12, sm: 4 }} key={t.value}>
              <Paper
                elevation={0}
                onClick={() => setForm((p) => ({ ...p, offer_type: t.value, line_ids: [] }))}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: "2px solid",
                  borderColor: selected ? brand.primary : "divider",
                  bgcolor: selected ? "rgba(31, 91, 64, 0.08)" : "background.paper",
                  "&:hover": {
                    borderColor: brand.primary,
                    bgcolor: "rgba(31, 91, 64, 0.06)"
                  },
                  position: "relative"
                }}
              >
                {selected && (
                  <CheckCircleIcon
                    sx={{ position: "absolute", top: 10, right: 10, color: brand.primary, fontSize: 20 }}
                  />
                )}
                <Typography variant="subtitle2" fontWeight={700} color={selected ? "primary.main" : "text.primary"}>
                  {t.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {offerTypeHints[t.value]}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function ImageUploadBox({ label, fieldName, file, existingUrl, onFileChange, onClear }) {
  const preview = file ? URL.createObjectURL(file) : existingUrl;
  
  return (
    <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 2, textAlign: "center", bgcolor: "background.paper" }}>
      <Typography variant="subtitle2" gutterBottom color="text.secondary">{label}</Typography>
      {preview ? (
        <Box sx={{ position: "relative", display: "inline-block", mt: 1 }}>
          <Box component="img" src={preview} alt="preview" sx={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 1 }} />
          <Button
            size="small"
            color="error"
            onClick={onClear}
            sx={{ position: "absolute", top: -8, right: -8, bgcolor: "rgba(255,255,255,0.9)", minWidth: "auto", p: 0.5, borderRadius: "50%", boxShadow: 1, "&:hover": { bgcolor: "error.lighter" } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </Button>
        </Box>
      ) : (
        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ mt: 1 }}>
          Upload Image
          <input hidden type="file" accept="image/*" onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              onFileChange(fieldName, e.target.files[0]);
            }
          }} />
        </Button>
      )}
    </Box>
  );
}

function getStoreDisplayName(form, stores = []) {
  const store = stores.find((s) => getStoreId(s) === form.store_id);
  return store?.title_en || store?.name_en || "";
}

function StepBasicDetails({ form, setForm }) {
  const { currency } = useCurrency();
  const isCoupon = Boolean(form.coupon_code || form.is_coupon);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleStoreChange = (e) => {
    const store_id = e.target.value;
    setForm((p) => ({ ...p, store_id, line_ids: [] }));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Basic Information</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField fullWidth required label="Offer Name (English)" name="offer_name_en"
              value={form.offer_name_en} onChange={handleChange} placeholder="e.g. Weekend Special Discount" />
            <ImageUploadBox label="Offer Image (English)" fieldName="image_en"
              file={form.image_en} existingUrl={form.image_en_url}
              onFileChange={(f, file) => setForm((p) => ({ ...p, [f]: file }))}
              onClear={() => setForm((p) => ({ ...p, image_en: null, image_en_url: "" }))} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField fullWidth label="Offer Name (Arabic)" name="offer_name_ar"
              value={form.offer_name_ar} onChange={handleChange}
              inputProps={{ dir: "rtl" }} placeholder="اسم العرض بالعربية" />
            <ImageUploadBox label="Offer Image (Arabic)" fieldName="image_ar"
              file={form.image_ar} existingUrl={form.image_ar_url}
              onFileChange={(f, file) => setForm((p) => ({ ...p, [f]: file }))}
              onClear={() => setForm((p) => ({ ...p, image_ar: null, image_ar_url: "" }))} />
          </Stack>
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Description" name="description"
            value={form.description} onChange={handleChange}
            placeholder="Describe the offer details visible to customers…" />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>Offer Value Type</FormLabel>
            <ToggleButtonGroup exclusive value={form.offer_value_type}
              onChange={(_, v) => v && setForm((p) => ({ ...p, offer_value_type: v }))}
              fullWidth size="small" sx={{ height: 40 }}>
              <ToggleButton value="percentage">% Percentage</ToggleButton>
              <ToggleButton value="fixed">{currency.symbol} Fixed Amount</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>Offer Value</FormLabel>
            <TextField fullWidth size="small" type="number" name="offer_value"
              value={form.offer_value} onChange={handleChange}
              sx={{ "& .MuiInputBase-root": { height: 40 } }}
              InputProps={{ endAdornment: <InputAdornment position="end">{form.offer_value_type === "percentage" ? "%" : currency.symbol}</InputAdornment> }} />
          </FormControl>
        </Grid>

        {form.offer_value_type === "percentage" && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth type="number" label="Max Discount Amount (Cap)"
              name="max_discount_amount" value={form.max_discount_amount} onChange={handleChange}
              placeholder={`Optional: maximum ${currency.symbol} discount`}
              InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
              helperText="Leave empty for no cap" />
          </Grid>
        )}

        {isCoupon && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth required label="Coupon Code" name="coupon_code"
              value={form.coupon_code}
              onChange={(e) => setForm((p) => ({ ...p, coupon_code: e.target.value.toUpperCase() }))}
              placeholder="e.g. SAVE20"
              inputProps={{ style: { fontFamily: "monospace", fontWeight: 700, letterSpacing: 2 } }}
              helperText="Alphanumeric, auto-uppercased" />
          </Grid>
        )}

        {!isCoupon && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Switch checked={form.auto_apply} onChange={(e) => setForm((p) => ({ ...p, auto_apply: e.target.checked }))} />}
              label={<Box><Typography variant="body2" fontWeight={600}>Auto Apply</Typography>
                <Typography variant="caption" color="text.secondary">Automatically applied without customer action</Typography></Box>} />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControlLabel
            control={<Switch checked={form.is_first_order} onChange={(e) => setForm((p) => ({ ...p, is_first_order: e.target.checked }))} />}
            label={<Box><Typography variant="body2" fontWeight={600}>First Order Only</Typography>
              <Typography variant="caption" color="text.secondary">Valid only for customer's first order</Typography></Box>} />
        </Grid>
      </Grid>
    </Box>
  );
}

function StepValidity({ form, setForm }) {
  const preset = form._duration_preset || "custom";

  const applyPreset = (nextPreset, fromDt) => {
    const from = fromDt ?? form.from_datetime;
    setForm((p) => {
      const next = { ...p, _duration_preset: nextPreset };
      if (!from) return next;
      if (nextPreset === "unlimited") next.to_datetime = dayjs(from).add(10, "year");
      else if (nextPreset === "24h")  next.to_datetime = dayjs(from).add(24, "hour");
      else if (nextPreset === "12h")  next.to_datetime = dayjs(from).add(12, "hour");
      return next;
    });
  };

  const handleFromChange = (val) => {
    setForm((p) => ({ ...p, from_datetime: val }));
    if (preset && preset !== "custom") applyPreset(preset, val);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Offer Validity Period</Typography>
      <Grid container spacing={3}>
        <Grid size={12}>
          <FormControl>
            <FormLabel sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>Duration Preset</FormLabel>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {DURATION_PRESETS.map((d) => {
                const active = preset === d.value;
                return (
                  <Paper key={d.value} elevation={0} onClick={() => applyPreset(d.value, null)}
                    sx={{ px: 2.5, py: 1.2, borderRadius: 2, cursor: "pointer", border: "2px solid",
                      borderColor: active ? "primary.main" : "divider",
                      bgcolor: active ? "primary.lighter" : "background.paper",
                      minWidth: 90, textAlign: "center" }}>
                    <Typography variant="body2" fontWeight={active ? 700 : 500} color={active ? "primary.main" : "text.secondary"}>
                      {d.label}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker label="Start Date & Time " value={form.from_datetime} onChange={handleFromChange}
            slotProps={{ textField: { fullWidth: true, required: true } }} disablePast />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker label="End Date & Time " value={form.to_datetime}
            onChange={(v) => setForm((p) => ({ ...p, to_datetime: v, _duration_preset: "custom" }))}
            slotProps={{ textField: { fullWidth: true, required: true } }}
            minDateTime={form.from_datetime || undefined}
            disabled={preset === "unlimited"} />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Usage Limits</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth type="number" label="Usage Limit Per User"
            name="usage_limit_per_user" value={form.usage_limit_per_user}
            onChange={(e) => setForm((p) => ({ ...p, usage_limit_per_user: e.target.value }))}
            placeholder="e.g. 1 (leave empty = unlimited)" />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth type="number" label="Total Usage Limit"
            name="total_usage_limit" value={form.total_usage_limit}
            onChange={(e) => setForm((p) => ({ ...p, total_usage_limit: e.target.value }))}
            placeholder="e.g. 500 (leave empty = unlimited)" />
        </Grid>
      </Grid>
    </Box>
  );
}

function StepConditions({ form, setForm }) {
  const { currency } = useCurrency();
  const togglePayment = (val) => {
    setForm((p) => {
      const current = p.accepted_payments || [];
      return {
        ...p,
        accepted_payments: current.includes(val)
          ? current.filter((v) => v !== val)
          : [...current, val],
      };
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Purchase Conditions</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth type="number" label="Minimum Purchase Amount"
            name="minimum_purchase_amount" value={form.minimum_purchase_amount}
            onChange={(e) => setForm((p) => ({ ...p, minimum_purchase_amount: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
            helperText="Minimum cart value required" />
        </Grid>

        <Grid size={12}>
          <FormControl component="fieldset">
            <FormLabel sx={{ fontWeight: 600, mb: 1.5 }}>Accepted Payment Methods</FormLabel>
            <FormGroup row>
              {PAYMENT_OPTIONS.map((p) => (
                <FormControlLabel key={p.value}
                  control={<Checkbox checked={(form.accepted_payments || []).includes(p.value)} onChange={() => togglePayment(p.value)} />}
                  label={p.label} sx={{ mr: 3 }} />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={<Switch checked={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked }))} />}
            label="Activate Immediately" />
        </Grid>
      </Grid>
    </Box>
  );
}

function StepOfferDetails({ form, setForm, stores, loadingStores }) {
  return (
    <Stack spacing={4}>
      <StepBasicDetails form={form} setForm={setForm} />
      <Divider />
      <StepValidity form={form} setForm={setForm} />
      <Divider />
      <StepConditions form={form} setForm={setForm} />
    </Stack>
  );
}

function StepTarget({ form, setForm }) {
  if (form.offer_type === "discount") {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Discount Offer
        </Typography>
        <Paper sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography color="text.secondary">
            This discount applies to the whole cart. No product or category lines are required.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return <OfferTargetSelect form={form} setForm={setForm} requireStore />;
}

function OfferSummary({ form, stores }) {
  const { currency } = useCurrency();
  const ot = getOfferType(form.offer_type);
  const storeLabel = getStoreDisplayName(form, stores);

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Summary
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={1.5}>
        <Chip label={ot.label} size="small" variant="outlined" sx={offerTypeChipSx} />
        {storeLabel && (
          <Typography variant="body2" color="text.secondary">
            Store: <strong>{storeLabel}</strong>
          </Typography>
        )}
        {form.offer_name_en && (
          <Typography variant="body1" fontWeight={700}>
            {form.offer_name_en}
          </Typography>
        )}
        {form.offer_value && (
          <Typography variant="h5" sx={{ color: brand.primary }} fontWeight={800}>
            {form.offer_value_type === "percentage" ? (
              `${form.offer_value}% OFF`
            ) : (
              <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                <CurrencyAmount value={form.offer_value} variant="h5" fontWeight={800} color="primary.main" />
                <Typography component="span" variant="inherit" fontWeight={800} sx={{ color: brand.primary }}>
                  OFF
                </Typography>
              </Stack>
            )}
            {form.offer_value_type === "percentage" ? `${form.offer_value}% OFF` : `${currency.symbol} ${form.offer_value} OFF`}
          </Typography>
        )}
        {(form.from_datetime || form.to_datetime) && (
          <Typography variant="caption" fontWeight={600}>
            {form.from_datetime ? dayjs(form.from_datetime).format("DD MMM YYYY HH:mm") : "—"} →{" "}
            {form.to_datetime ? dayjs(form.to_datetime).format("DD MMM YYYY HH:mm") : "—"}
          </Typography>
        )}
        {form.offer_type !== "discount" && (
          <Typography variant="caption" color="text.secondary">
            {form.line_ids.length} line{form.line_ids.length === 1 ? "" : "s"} selected
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export default function OfferForm() {
  const navigate = useNavigate();
  const { showToast, showApiError } = useToast();

  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);

  useEffect(() => {
    api
      .get("/store/list")
      .then((res) => {
        const data = res.data?.data ?? res.data;
        const list = Array.isArray(data) ? data : [];
        setStores(list);
        const firstId = getStoreId(list[0]);
        if (firstId) {
          setForm((p) => (p.store_id ? p : { ...p, store_id: firstId }));
        }
      })
      .catch(() => setStores([]))
      .finally(() => setLoadingStores(false));
  }, []);

  const validateStep = useCallback(
    (step) => {
      switch (step) {
        case 0:
          return !!form.offer_type;
        case 1:
          return !!(
            form.store_id &&
            form.offer_name_en.trim() &&
            form.offer_value &&
            form.from_datetime &&
            form.to_datetime &&
            dayjs(form.to_datetime).isAfter(form.from_datetime)
          );
        case 2:
          if (form.offer_type === "item" || form.offer_type === "category") {
            return form.line_ids.length > 0;
          }
          return true;
        default:
          return true;
      }
    },
    [form]
  );

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      showToast("Please complete all required fields in this step.", { severity: "error" });
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setActiveStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      showToast("Please complete the required target selection.", { severity: "error" });
      return;
    }

    setSubmitting(true);

    try {
      await api.post("/offer/create", buildOfferPayload(form));
      navigate("/offer/list");
    } catch (err) {
      console.error("Submit error:", err);
      showApiError(err, "Save failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 12}}>
            <MainCard
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocalOfferIcon color="primary" />
                  <Typography variant="h6">Create Offer</Typography>
                </Box>
              }
              contentSX={{ p: 3 }}
              sx={{ borderRadius: 3 }}
            >
              <>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {STEPS.map((s, i) => (
                      <Step key={i} completed={i < activeStep}>
                        <StepLabel
                          StepIconComponent={({ active, completed }) => (
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: completed ? "success.main" : active ? "primary.main" : "grey.200",
                                color: completed || active ? "#fff" : "text.secondary",
                                fontSize: 14
                              }}
                            >
                              {completed ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : i + 1}
                            </Avatar>
                          )}
                        >
                          <Typography variant="caption" fontWeight={activeStep === i ? 700 : 400}>
                            {s.label}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  <Box sx={{ minHeight: 320 }}>
                    {[
                      <StepOfferType key={0} form={form} setForm={setForm} />,
                      <StepOfferDetails
                        key={1}
                        form={form}
                        setForm={setForm}
                        stores={stores}
                        loadingStores={loadingStores}
                      />,
                      <StepTarget key={2} form={form} setForm={setForm} />
                    ][activeStep]}
                  </Box>

                  <Box
                    sx={{
                      mt: 4,
                      pt: 3,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "space-between"
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={activeStep === 0 ? () => navigate("/offer/list") : handleBack}
                      disabled={submitting}
                      sx={offerCancelButtonSx}
                    >
                      {activeStep === 0 ? "Cancel" : "Back"}
                    </Button>
                    {activeStep < 2 ? (
                      <Button
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleNext}
                        sx={offerPrimaryButtonSx}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={
                          submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />
                        }
                        sx={offerPrimaryButtonSx}
                      >
                        {submitting ? "Saving…" : "Create Offer"}
                      </Button>
                    )}
                  </Box>
              </>
            </MainCard>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2} sx={{ position: "sticky", top: 20 }}>
                {form.offer_type && <OfferSummary form={form} stores={stores} />}
              </Stack>
            </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
